import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, eur, errMsg } from "@/lib/api";
import { usePlan } from "@/context/PlanContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, FileDown, AlertTriangle, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Import del menu da Excel o CSV (funzione del piano Pro). */
export const MenuImport = ({ onDone }) => {
  const { has, askUpgrade, handleError } = usePlan();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [prev, setPrev] = useState(null);
  const [mode, setMode] = useState("add");
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  const send = async (dry) => {
    if (!file) return toast.error("Scegli un file .pdf, .xlsx o .csv");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/menu/import?dry_run=${dry}&mode=${mode}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (dry) setPrev(data);
      else {
        toast.success(`${data.imported} piatti importati${data.updated ? ` · ${data.updated} prezzi completati` : ""}`);
        setOpen(false);
        setFile(null);
        setPrev(null);
        onDone?.();
      }
    } catch (e) {
      if (!handleError(e)) toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="h-11"
        data-testid="menu-import-btn"
        onClick={() => (has("menu_import") ? setOpen(true) : askUpgrade("menu_import", "L'importazione del menu da file è inclusa nel piano Pro", "pro"))}
      >
        <Upload className="mr-1.5 h-4 w-4" /> Importa menu
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setFile(null); setPrev(null); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="menu-import-dialog">
          <DialogHeader>
            <DialogTitle>Importa il menu da PDF, Excel o CSV</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Colonne (Excel/CSV): <span className="font-medium">nome, categoria, prezzo, reparto, portata, allergeni, descrizione</span>.
              Bastano nome e prezzo.
            </p>
            <p className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500" data-testid="pdf-import-note">
              Dal <span className="font-semibold">PDF</span> del tuo menu leggo le righe con il prezzo e uso i titoli come
              categorie: controlla l'anteprima prima di confermare. I PDF fatti da foto o scansioni non si possono leggere.
            </p>
            <Button variant="outline" className="w-full" data-testid="download-template-btn" onClick={() => window.open(`${API}/menu/import/template.csv`, "_blank")}>
              <FileDown className="mr-1.5 h-4 w-4" /> Scarica il modello di esempio
            </Button>

            <input
              ref={input}
              type="file"
              accept=".csv,.xlsx,.xlsm,.pdf"
              className="hidden"
              data-testid="menu-file-input"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                setPrev(null);
              }}
            />
            <button
              onClick={() => input.current?.click()}
              data-testid="pick-file-btn"
              className="w-full rounded-lg border-2 border-dashed border-slate-300 p-6 text-center text-sm text-slate-500 hover:border-slate-400"
            >
              {file ? <span className="font-semibold text-slate-900">{file.name}</span> : "Tocca per scegliere il file del menu"}
            </button>

            <div className="flex gap-2">
              {[["add", "Aggiungi ai piatti esistenti"], ["replace", "Sostituisci tutto il menu"]].map(([k, l]) => (
                <Button key={k} size="sm" variant={mode === k ? "default" : "outline"} onClick={() => { setMode(k); setPrev(null); }} data-testid={`import-mode-${k}`}>
                  {l}
                </Button>
              ))}
            </div>

            {prev && (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid="import-preview">
                <p className="text-sm font-semibold">
                  {prev.count} righe lette · <span className="text-emerald-600">{prev.will_insert} da inserire</span>
                  {prev.duplicates > 0 && <span className="text-slate-500"> · {prev.duplicates} già presenti</span>}
                  {prev.updates > 0 && <span className="text-amber-700"> · {prev.updates} prezzi da completare</span>}
                </p>
                {prev.errors.length > 0 && (
                  <div className="rounded bg-amber-50 p-2" data-testid="import-errors">
                    {prev.errors.map((e, i) => (
                      <p key={`err-${e}`} className="flex items-start gap-1.5 text-[11px] text-amber-800">
                        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {e}
                      </p>
                    ))}
                  </div>
                )}
                <div className="max-h-40 divide-y divide-slate-100 overflow-y-auto">
                  {prev.rows.slice(0, 30).map((r, i) => (
                    <p key={`${r.name}-${r.price}-${i}`} className="flex items-center justify-between py-1 text-xs">
                      <span className="truncate">{r.name} <span className="text-slate-400">· {r.category} · {r.department}</span></span>
                      <span className="font-num shrink-0 font-semibold">{eur(r.price)}</span>
                    </p>
                  ))}
                </div>
              </div>
            )}

            {prev ? (
              <Button className="h-12 w-full" disabled={busy} onClick={() => send(false)} data-testid="confirm-import-btn">
                <Check className="mr-1.5 h-4 w-4" /> Importa {prev.will_insert} piatti
              </Button>
            ) : (
              <Button className="h-12 w-full" disabled={busy || !file} onClick={() => send(true)} data-testid="check-import-btn">
                Controlla il file
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
